import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-search-2-line',
    title: 'Diagnóstico Estratégico',
    desc: 'Realizamos uma análise aprofundada da situação jurídica e empresarial do cliente, identificando riscos, oportunidades e prioridades de atuação.',
  },
  {
    number: '02',
    icon: 'ri-scales-line',
    title: 'Análise Jurídica',
    desc: 'Nossa equipe conduz pesquisa técnica especializada, mapeando precedentes, legislação aplicável e cenários possíveis com precisão.',
  },
  {
    number: '03',
    icon: 'ri-lightbulb-flash-line',
    title: 'Elaboração de Soluções',
    desc: 'Desenvolvemos estratégias jurídicas personalizadas, apresentando caminhos claros, viáveis e alinhados aos objetivos do cliente.',
  },
  {
    number: '04',
    icon: 'ri-checkbox-circle-line',
    title: 'Execução e Acompanhamento',
    desc: 'Implementamos a estratégia com rigor e acompanhamos cada etapa do processo, garantindo transparência, agilidade e resultados efetivos.',
  },
];

export default function HowWeWork() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="como-atuamos" className="bg-kpr-medium py-28 lg:py-36 relative overflow-hidden" ref={ref}>
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20texture%20background%20with%20subtle%20geometric%20patterns%2C%20very%20dark%20near-black%20surface%20with%20faint%20grain%20texture%2C%20minimal%20and%20sophisticated%2C%20premium%20corporate%20background%2C%20ultra-dark%20matte%20surface%20photography&width=1920&height=900&seq=hw001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
      </div>
      <div className="absolute inset-0 bg-kpr-medium/85" />

      <div className="relative max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-20">
          <div>
            <div className={`flex items-center gap-4 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="w-10 h-px bg-kpr-gold" />
              <span className="text-kpr-gold text-xs font-medium tracking-[0.25em] uppercase">Metodologia</span>
            </div>
            <h2 className={`font-serif text-4xl md:text-5xl text-white font-light leading-[1.1] transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              Como Atuamos
            </h2>
          </div>
          <p className={`text-white/45 text-[15px] leading-loose font-light transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Nossa abordagem combina rigor técnico com visão estratégica, seguindo um processo
            estruturado para garantir os melhores resultados em cada caso.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/8">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className="group relative p-8 border-r border-white/8 last:border-r-0 hover:bg-white/3 transition-all duration-300"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.6s ease ${i * 150}ms, transform 0.6s ease ${i * 150}ms`,
              }}
            >
              {/* Step number */}
              <div className="font-serif text-kpr-gold/20 text-6xl font-light leading-none mb-6 select-none group-hover:text-kpr-gold/30 transition-colors duration-300">
                {step.number}
              </div>

              <div className="font-serif text-white/90 text-base font-light mb-3 leading-snug">{step.title}</div>
              <p className="text-white/40 text-sm leading-relaxed font-light">{step.desc}</p>

              {/* Bottom accent */}
              <div className="absolute bottom-0 left-0 h-px w-0 bg-kpr-gold group-hover:w-full transition-all duration-400" />
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div
          className={`mt-16 border border-white/10 p-10 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 transition-all duration-700 delay-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <div className="w-10 h-px bg-kpr-gold mb-5" />
            <h3 className="font-serif text-white text-2xl md:text-3xl font-light mb-3">
              Pronto para dar o próximo passo?
            </h3>
            <p className="text-white/45 text-sm font-light">
              Fale com nossos especialistas e inicie seu diagnóstico estratégico.
            </p>
          </div>
          <a
            href="https://wa.me/5541992570505?text=Ol%C3%A1%2C%20quero%20agendar%20uma%20consulta%20estrat%C3%A9gica%20com%20a%20KPR%20Advogados."
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2.5 bg-kpr-gold hover:bg-kpr-gold-light text-kpr-dark font-semibold text-xs tracking-widest uppercase px-10 py-4 transition-all duration-300 whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className="ri-whatsapp-line text-base w-4 h-4 flex items-center justify-center" />
            Agendar Consulta
          </a>
        </div>
      </div>
    </section>
  );
}
